import { forwardRef } from 'react'
import dayjs from 'dayjs'
import { NumericFormat, NumericFormatProps } from 'react-number-format'

export const pricingUnitsOptions = [
  'Users',
  'Devices',
  'Endpoints',
  'Servers',
  'Seats',
  'GB of Data',
  'TB of Data',
  'API calls',
  'Assets',
  'Events',
  'IP Addresses'
]

/*
 * Helper to get the date range for a specific period (year index)
 * */
export const getPeriodDateRange = (
  startDate: string,
  endDate: string,
  periodIndex: number
): { periodStart: string; periodEnd: string } => {
  const start = dayjs(startDate)
  const end = dayjs(endDate)

  // Period start: startDate + periodIndex years
  const periodStart = start.add(periodIndex, 'year')

  // Period end: min(startDate + (periodIndex + 1) years, endDate)
  const nextAnniversary = start.add(periodIndex + 1, 'year')
  const periodEnd = nextAnniversary.isBefore(end) ? nextAnniversary : end

  return {
    periodStart: periodStart.format('DD/MM/YYYY'),
    periodEnd: periodEnd.format('DD/MM/YYYY')
  }
}

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void
  name: string
}

export const NumericFormatCustom = forwardRef<NumericFormatProps, CustomProps>(function NumericFormatCustom(
  props,
  ref
) {
  const { onChange, ...other } = props

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value
          }
        })
      }}
      thousandSeparator
      valueIsNumericString
    />
  )
})

/*
 * Calculate number of contract periods based on start date anniversary
 * e.g., 15/12/2025 to 15/12/2026 = 1 period (exactly 1 year, only 2025)
 * e.g., 15/12/2025 to 16/12/2026 = 2 periods (1 year + 1 day, 2025 and 2026)
 * */
export const calculateContractPeriods = (startDate: string, endDate: string): number => {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const startYear = start.getUTCFullYear()
  const endYear = end.getUTCFullYear()

  // Calculate years difference
  let periods = endYear - startYear

  // Check if end date has passed the anniversary in the end year
  // Compare month and day using UTC methods for timezone consistency
  const startMonth = start.getUTCMonth()
  const startDay = start.getUTCDate()
  const endMonth = end.getUTCMonth()
  const endDay = end.getUTCDate()

  // End date is past the anniversary if it's in a later month,
  // or same month but later day (not equal - equal means exactly on anniversary)
  const isPastAnniversary = endMonth > startMonth || (endMonth === startMonth && endDay > startDay)

  if (isPastAnniversary) {
    periods += 1
  }

  return Math.max(1, periods)
}
